import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Leaf, Check, ArrowRight, XCircle } from "lucide-react";
import Logo from "../components/Logo";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();

  const [phase, setPhase] = useState("checking"); // checking | paid | error | expired
  const [email, setEmail] = useState("");
  const [accountExists, setAccountExists] = useState(false);
  const attempts = useRef(0);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // PayPal flow: payment already captured on the landing page
    if (location.state?.paypal && location.state?.email) {
      setEmail(location.state.email);
      setAccountExists(!!location.state.accountExists);
      setPhase("paid");
      return;
    }
    if (!sessionId) {
      setPhase("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (attempts.current >= 8) {
        setPhase("error");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setEmail(data.email || "");
        if (data.payment_status === "paid") {
          setAccountExists(data.account_exists);
          setPhase("paid");
          return;
        }
        if (data.status === "expired") {
          setPhase("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, location.state]);

  const submitRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    setSubmitting(true);
    try {
      await register(name.trim(), email, password);
      toast.success("¡Cuenta creada! Bienvenida a tu programa.");
      navigate("/programa");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream grain flex flex-col">
      <div className="max-w-6xl w-full mx-auto px-5 sm:px-8 py-6">
        <Link to="/"><Logo /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white border border-forest/10 rounded-3xl p-8 sm:p-10">
          {phase === "checking" && (
            <div className="text-center py-6" data-testid="payment-checking">
              <Leaf className="w-9 h-9 text-clay animate-pulse mx-auto" />
              <h1 className="mt-5 font-serif text-3xl">Confirmando tu pago…</h1>
              <p className="mt-2 text-forest/60">Un momento, estamos verificando la transacción.</p>
            </div>
          )}

          {phase === "paid" && !accountExists && (
            <div data-testid="payment-register">
              <div className="w-14 h-14 rounded-full bg-sage flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-clay" />
              </div>
              <h1 className="mt-5 font-serif text-3xl text-center">¡Pago confirmado!</h1>
              <p className="mt-2 text-center text-forest/60">Crea tu cuenta para acceder a tu programa de 4 semanas.</p>
              <form onSubmit={submitRegister} className="mt-8 space-y-4">
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-semibold text-forest/60">Tu nombre</label>
                  <input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Carmen" className="mt-2 w-full rounded-full bg-sage/60 border border-transparent focus:border-clay focus:bg-white outline-none px-5 py-3 transition-colors" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-semibold text-forest/60">Email</label>
                  <input data-testid="register-email-input" value={email} readOnly className="mt-2 w-full rounded-full bg-sage/40 border border-transparent px-5 py-3 text-forest/70" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-semibold text-forest/60">Contraseña</label>
                  <input data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Mínimo 6 caracteres" className="mt-2 w-full rounded-full bg-sage/60 border border-transparent focus:border-clay focus:bg-white outline-none px-5 py-3 transition-colors" />
                </div>
                <button data-testid="register-submit-button" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-clay text-cream font-medium px-6 py-4 hover:bg-clay-dark transition-colors disabled:opacity-60">
                  {submitting ? "Creando cuenta…" : "Acceder a mi programa"} <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          )}

          {phase === "paid" && accountExists && (
            <div className="text-center" data-testid="payment-account-exists">
              <div className="w-14 h-14 rounded-full bg-sage flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-clay" />
              </div>
              <h1 className="mt-5 font-serif text-3xl">Ya tienes una cuenta</h1>
              <p className="mt-2 text-forest/60">Inicia sesión para acceder a tu programa.</p>
              <Link to="/acceder" data-testid="go-login-link" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-clay text-cream font-medium px-6 py-3.5 hover:bg-clay-dark transition-colors">
                Iniciar sesión <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {(phase === "error" || phase === "expired") && (
            <div className="text-center" data-testid="payment-error">
              <XCircle className="w-12 h-12 text-clay mx-auto" />
              <h1 className="mt-5 font-serif text-3xl">No pudimos confirmar el pago</h1>
              <p className="mt-2 text-forest/60">Si crees que se ha cobrado, contáctanos. Puedes intentarlo de nuevo.</p>
              <Link to="/" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-clay text-cream font-medium px-6 py-3.5 hover:bg-clay-dark transition-colors">
                Volver al inicio
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
